import type {
  SearchResultJobV5,
  Facets,
  SearchParams,
} from '@seek/chalice-types';
import { Box, Stack } from 'braid-design-system';
import type { ComponentProps } from 'react';

import { ModifyMySearchAccessabilityLink } from 'src/components/SearchResultPage/SplitView/AccessabilityLinks/AccessabilityLinks';
import { useAppConfig } from 'src/config/appConfig';
import { useInFlowNudgingFeatureFlag } from 'src/store/featureFlags/hooks';
import { useSelector } from 'src/store/react';
import {
  selectFacets,
  selectCanonicalCompany,
  selectClearCompanyRefinements,
  selectIsRadialFilterNudgeShown,
  selectSearchQuery,
  selectResultsCount,
} from 'src/store/selectors';

import CompanyResults from '../CompanyResults/CompanyResults';
import CompanySummary from '../CompanySummary/CompanySummary';
import { compareJobIds } from '../SplitView/helpers';

import { JobListItem } from './JobListItem/JobListItem';
import DistanceFromNudge from './Nudges/DistanceFromNudge/DistanceFromNudge';
import { NudgeIntent } from './Nudges/NudgeIntent/NudgeIntent';
import ProfileNudge from './Nudges/ProfileNudge/ProfileNudge';
import { useProfileNudge } from './Nudges/ProfileNudge/useProfileNudge';
import {
  FACET_GROUP_INFO,
  FACET_ORDER,
  SALARY_NUDGE_SUPPORTED_COUNTRIES,
} from './constants';

interface SearchResultListProps
  extends Pick<ComponentProps<typeof JobListItem>, 'setCurrentJob'> {
  jobs: SearchResultJobV5[];
  isCompanySearch: boolean;
  selectedJobId?: string;
  currentPageNumber?: number;
  isNewToYou?: boolean;
}

const getIndexOfFirstNonFeatured = (jobs: SearchResultJobV5[]): number =>
  jobs.findIndex((job) => !job.isFeatured);

const SearchResultList = ({
  jobs = [],
  setCurrentJob,
  selectedJobId,
  isCompanySearch,
  currentPageNumber,
  isNewToYou,
}: SearchResultListProps) => {
  const { nudge: profileNudge } = useProfileNudge();
  const { country: countryCode } = useAppConfig();
  const shouldShowProfileNudge = Boolean(profileNudge);
  const shouldShowDistanceFromNudge = useSelector(
    selectIsRadialFilterNudgeShown,
  );
  const canonicalCompany = useSelector(selectCanonicalCompany);
  const clearCompanyRefinements =
    useSelector(selectClearCompanyRefinements) || false;
  const totalJobCount = useSelector(selectResultsCount);
  const companyName = canonicalCompany?.description;

  const profileNudgePosition = getIndexOfFirstNonFeatured(jobs) + 1;

  const jobList = jobs.map((job, index) => (
    <JobListItem
      position={index + 1}
      key={`${job.id}-${job.displayType}`}
      job={job}
      solMetadata={job.solMetadata}
      setCurrentJob={setCurrentJob}
      isSelected={compareJobIds(selectedJobId, job.id)}
    />
  ));

  const { inFlowNudgingWithoutCounts, inFlowNudgingWithCounts } =
    useInFlowNudgingFeatureFlag();
  const facetsData = useSelector(selectFacets);
  const searchQuery = useSelector(selectSearchQuery);
  const displayOnFirstPage = currentPageNumber === 1;

  if (
    (inFlowNudgingWithoutCounts || inFlowNudgingWithCounts) &&
    displayOnFirstPage &&
    !isNewToYou &&
    totalJobCount !== null &&
    totalJobCount > 50 &&
    facetsData &&
    Object.keys(facetsData).length > 0
  ) {
    const filteredFacetsData = Object.fromEntries(
      Object.entries(facetsData as Facets).map(([facetKey, facetArray]) => [
        facetKey,
        facetArray,
      ]),
    );

    const facetDisplays = Object.entries(filteredFacetsData as Facets)
      .filter(([key]) => key in FACET_GROUP_INFO) // filter out facets that are not specified
      .map(([key, data]) => {
        const facetKey = key as keyof typeof FACET_GROUP_INFO;
        return {
          key: facetKey,
          data,
          title: FACET_GROUP_INFO[facetKey].title,
          description: FACET_GROUP_INFO[facetKey].description,
          isMultiSelect: FACET_GROUP_INFO[facetKey].isMultiSelect,
          searchQueryKey: FACET_GROUP_INFO[facetKey].searchQueryKey,
        };
      })
      .filter(
        (facet) =>
          facet.key !== 'salaryMin' ||
          SALARY_NUDGE_SUPPORTED_COUNTRIES.includes(countryCode),
      )
      .filter((facet) => {
        const queryKey = facet.searchQueryKey;
        // if facet is in searchQuery, it will return false and nudge is filtered out
        return !searchQuery[queryKey as keyof SearchParams];
      });

    const sortedFacetDisplays = facetDisplays.sort((a, b) => {
      const indexA = FACET_ORDER.indexOf(a.key);
      const indexB = FACET_ORDER.indexOf(b.key);
      return indexA - indexB;
    });

    const nudgeInterval = 4;
    const maxNudges = Math.min(
      sortedFacetDisplays.length,
      Math.floor(jobList.length / nudgeInterval),
    );
    for (let i = 1; i <= maxNudges; i++) {
      const insertIndex = i * nudgeInterval + (i - 1);
      const facet = sortedFacetDisplays[i - 1];
      const displayToastMessage = 'Filters updated';
      jobList.splice(
        insertIndex,
        0,
        <NudgeIntent
          key={`nudge-${facet.key}-${insertIndex}`}
          facetKey={facet.key}
          facetTypesData={facet.data}
          title={facet.title}
          description={facet.description}
          displayToastMessage={displayToastMessage}
          isMultiSelect={facet.isMultiSelect}
        />,
      );
    }
  }

  // Only enabled existing nudges if the nudge intent is not enabled
  if (!inFlowNudgingWithoutCounts || !inFlowNudgingWithCounts) {
    if (shouldShowProfileNudge) {
      jobList.splice(
        profileNudgePosition,
        0,
        <ProfileNudge key="ProfileNudge" position={profileNudgePosition} />,
      );
    }

    if (shouldShowDistanceFromNudge) {
      const distanceFromNudgePosition = Math.floor(jobList.length / 2);

      jobList.splice(
        distanceFromNudgePosition,
        0,
        <DistanceFromNudge
          key="DistanceFromNudge"
          position={distanceFromNudgePosition}
        />,
      );
    }
  }

  return (
    <>
      {isCompanySearch && companyName ? (
        <Box paddingBottom="medium">
          <Stack space="small">
            <CompanySummary companyName={companyName} />
            {typeof clearCompanyRefinements === 'object' ? (
              <CompanyResults
                isCompanySearch={isCompanySearch}
                clearCompanyRefinements={clearCompanyRefinements}
                jobs={jobs || []}
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <ModifyMySearchAccessabilityLink placement="job-list" position="top" />

      <Stack space="xsmall">{jobList}</Stack>

      <ModifyMySearchAccessabilityLink placement="job-list" position="bottom" />
    </>
  );
};

export default SearchResultList;
